import React, { useState } from 'react';

import { Menu } from '@mui/material';
import type { DocumentStatusFilterType } from 'src/components';
import {
  DocumentStatusFilter,
  NameDialog,
  NameDialogVariant,
  OptionsMenuItem,
  PrimaryPlusIconButton,
} from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { isDev, isStaging } from 'src/config';
import { Apollo } from 'src/gql/generated';
import { useCreateAndOpenNewDocument } from 'src/hooks';

import { JsonFileUploader } from './components/JsonFileUploader';

interface RepositoryNewButtonProps {
  documentStatusFilter: DocumentStatusFilterType;
  disabled?: boolean;
  repositoryId?: UUID;
}

export function RepositoryNewButton({
  disabled = false,
  documentStatusFilter: _documentStatusFilter,
  repositoryId,
}: RepositoryNewButtonProps): JSX.Element | null {
  const [open, setOpen] = useState<boolean>(false);
  const [jsonPickerOpen, setJsonPickerOpen] = useState(false);
  const { createAndOpenNewDocument } = useCreateAndOpenNewDocument();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [newDocumentVariant, setNewDocumentVariant] = useState<Apollo.DocumentVariant | null>(null);
  const handleToggleOpen = useAccessGuard({
    callback: () => setOpen(currentOpen => !currentOpen),
    minRole: Apollo.OrgRole.Basic,
  });

  const isJsonUploaderAvailable = isDev || isStaging;

  return (
    <>
      <PrimaryPlusIconButton
        ref={setAnchorEl}
        data-cy="repository-new-button"
        disabled={disabled}
        tooltip="New Document"
        onClick={handleToggleOpen}
      />
      <Menu
        keepMounted
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setNewDocumentVariant(Apollo.DocumentVariant.Template);
          }}
          icon={<TemplateIcon />}
          text="Template"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setNewDocumentVariant(Apollo.DocumentVariant.Document);
          }}
          icon={<DocumentIcon />}
          text="Document"
        />
        {isJsonUploaderAvailable && (
          <OptionsMenuItem
            clickHandler={() => {
              setOpen(false);
              setJsonPickerOpen(true);
            }}
            icon={<UploadIcon />}
            text="Upload JSON (DEV/STAGING ONLY)"
          />
        )}
      </Menu>
      <NameDialog
        handleClose={() => setNewDocumentVariant(null)}
        open={Boolean(newDocumentVariant)}
        placeholder="Add a title for your document..."
        variant={NameDialogVariant.CreateDocument}
        onFormikSubmit={async name => {
          if (!newDocumentVariant) return;

          await createAndOpenNewDocument({
            afterOpen: () => setNewDocumentVariant(null),
            name,
            reportId: repositoryId,
            variant: newDocumentVariant,
          });
        }}
      />
      {isJsonUploaderAvailable && (
        <JsonFileUploader
          isOpen={jsonPickerOpen}
          repositoryId={repositoryId}
          onClose={() => setJsonPickerOpen(false)}
        />
      )}
    </>
  );
}

export function statusToDocumentVariant(
  documentStatusFilter: DocumentStatusFilterType
): Apollo.DocumentVariant {
  switch (documentStatusFilter) {
    case DocumentStatusFilter.Draft:
    case DocumentStatusFilter.All:
    case DocumentStatusFilter.Mixed:
    case DocumentStatusFilter.Signed:
    case Apollo.DocumentStatus.Expired:
      return Apollo.DocumentVariant.Document;
    case DocumentStatusFilter.Template:
      return Apollo.DocumentVariant.Template;
    default:
      return Apollo.DocumentVariant.Document;
  }
}
